import { type ChangeEvent, type ReactNode } from "react";
import { vars } from "@pack-it-up/ui-web";
import { formatOffset } from "@pack-it-up/core";
import type { RelativeOffset, TimeUnit } from "@pack-it-up/core";

// Doc 07 §6 — "relative to start" control for template todos.
// A value stepper, unit select, and before/after toggle. Renders
// the human-readable label via `formatOffset` below the controls.

export interface RelativeDueControlProps {
  value: RelativeOffset | undefined;
  onChange: (offset: RelativeOffset | undefined) => void;
}

const UNITS: { value: TimeUnit; label: string }[] = [
  { value: "day", label: "day(s)" },
  { value: "week", label: "week(s)" },
  { value: "month", label: "month(s)" },
  { value: "year", label: "year(s)" },
];

const controlStyle = {
  display: "flex",
  flexDirection: "column" as const,
  gap: vars.space.xs,
};

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: vars.space.sm,
};

const inputStyle = {
  width: "56px",
  padding: "5px 8px",
  borderRadius: vars.radius.sm,
  border: `1px solid ${vars.color.border}`,
  background: vars.color.background,
  color: vars.color.text,
  fontFamily: vars.font.body,
  fontSize: vars.font.sizeSm,
  textAlign: "center" as const,
};

const selectStyle = {
  padding: "5px 8px",
  borderRadius: vars.radius.sm,
  border: `1px solid ${vars.color.border}`,
  background: vars.color.background,
  color: vars.color.text,
  fontFamily: vars.font.body,
  fontSize: vars.font.sizeSm,
  cursor: "pointer",
};

const labelStyle = {
  fontSize: vars.font.sizeXs,
  color: vars.color.textMuted,
  fontStyle: "italic" as const,
};

export const RelativeDueControl = ({ value, onChange }: RelativeDueControlProps): ReactNode => {
  // Defaults when no offset is set yet.
  const absValue = value !== undefined ? Math.abs(value.value) : 0;
  const unit: TimeUnit = value?.unit ?? "day";
  // negative = before start; positive = after start
  const direction = value !== undefined && value.value >= 0 ? "after" : "before";

  const handleAbsChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const n = parseInt(e.target.value, 10);
    if (isNaN(n) || n < 0) return;
    const newVal = direction === "before" ? -n : n;
    onChange(n === 0 ? undefined : { value: newVal, unit });
  };

  const handleUnitChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const newUnit = e.target.value as TimeUnit;
    if (absValue === 0) {
      onChange(undefined);
      return;
    }
    const newVal = direction === "before" ? -absValue : absValue;
    onChange({ value: newVal, unit: newUnit });
  };

  const handleDirectionChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const newDir = e.target.value as "before" | "after";
    if (absValue === 0) return;
    const newVal = newDir === "before" ? -absValue : absValue;
    onChange({ value: newVal, unit });
  };

  const previewLabel =
    value !== undefined && value.value !== 0 ? formatOffset(value) : "No relative due date";

  return (
    <div style={controlStyle}>
      <div style={rowStyle}>
        <input
          type="number"
          min={0}
          value={absValue}
          onChange={handleAbsChange}
          aria-label="Offset amount"
          style={inputStyle}
        />
        <select
          value={unit}
          onChange={handleUnitChange}
          aria-label="Offset unit"
          style={selectStyle}
        >
          {UNITS.map((u) => (
            <option key={u.value} value={u.value}>
              {u.label}
            </option>
          ))}
        </select>
        <select
          value={direction}
          onChange={handleDirectionChange}
          aria-label="Before or after start"
          style={selectStyle}
        >
          <option value="before">before start</option>
          <option value="after">after start</option>
        </select>
      </div>
      <span style={labelStyle} aria-live="polite">
        {previewLabel}
      </span>
    </div>
  );
};
